import React, { useEffect, useState } from 'react';
import './DeadlineDocket.scss';
import filterFactory from '../../../components/react-bootstrap-table-next/react-bootstrap-table2-filter';
import BootstrapTable from '../../../components/react-bootstrap-table-next/react-bootstrap-table2';
import paginationFactory from '../../../components/react-bootstrap-table-next/react-bootstrap-table2-paginator';
import { TimelineEvent } from '../../../slices/marketplace/timeline/models/timeline-event.model';
import {
    formatBusinessDatePt,
    getEventDeadlineDateUtc,
} from '../../../slices/marketplace/timeline/utils/timeline-dates';
import { useDispatch } from 'react-redux';
import { useStateSelector } from '../../../store/selectors';
import { GetCurrentEvents } from '../../../slices/marketplace/timeline/thunks/get-current-events.thunk';
import { TableFilter } from '../../../components/Table/models/table-filter';
import Helpers from '../../../utils/helper';
import { TimelineParty } from '../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { MarketplaceOpportunityStatus } from '../../../slices/marketplace/opportunity/models/opportunity-status';
import { useLocation } from 'react-router-dom';
import { LinkedDataRequestProject } from '../../../slices/marketplace/timeline/models/linked-data-request-project.model';
import { useCurrentMarketplaceParty } from '../../../custom-hooks/useCurrentMarketplaceParty';
import { isEventActionClickable } from '../EventActionAvailabilityHelper';
import { EngagementData } from '../../../slices/marketplace/timeline/models/engagement-data.model';
import { EngagementProcess } from '../../../slices/marketplace/timeline/models/engagement-process.model';
import { TimelineActionType } from '../../../slices/marketplace/timeline/models/timeline-action-type.model';
import { NotificationFlag } from '../../../components/NotificationFlag/NotificationFlag';
import { flagEventId, marketplaceFlagCodes } from '../../../notifications/notification-flag-codes';
import { DocketBuyerRow } from '../../../slices/marketplace/timeline/models/docket-buyer-row.model';
import { formatBuyerDisplayName } from '../Buyers/EngagementBuyers';
import { EngagementBuyerActionState } from '../../../slices/marketplace/timeline/models/engagement-buyer-action-state.model';
import { TimelineActionStatus } from '../../../slices/marketplace/timeline/models/enums/timeline-action-status.enum';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';

type DocketRow = TimelineEvent & { isExpanded: boolean; isClickable: boolean };

interface DeadlineDocketProps {
    engagementId: number;
    engagementStartDate: Date;
    onEventClick(id: string, timelineId?: number): void;
    viewedTimelineId?: number;
    buyerActionState?: EngagementBuyerActionState[];
    events: TimelineEvent[];
    engagementData: EngagementData;
    dataRequestProjects?: LinkedDataRequestProject[];
    opportunityStatus?: MarketplaceOpportunityStatus;
    notificationFlagPrefix: string;
    isReadonly?: boolean;
    engagementBuyerId?: number | null;
    isFlowOfFundsAvailable?: boolean;
    engagementProcess?: EngagementProcess;
}

const DeadlineDocket = (props: DeadlineDocketProps) => {
    const dispatch = useDispatch() as any;
    const location = useLocation();
    const currentTimelineEvents = useStateSelector(
        (state) => state.timelines.currentTimelineEvents
    );
    const [isLoading, setIsLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalSize, setTotalSize] = useState(1);
    const [sizePerPage, setSizePerPage] = useState(5);
    const [events, setEvents] = useState<TimelineEvent[]>([]);
    const [eventsParty, setEventsParty] = useState<TimelineParty>();

    const defaultSorted: [{ dataField: any; order: any }] = [
        {
            dataField: 'name',
            order: 'asc',
        },
    ];

    const [currentSort, setCurrentSort] = useState<TableFilter>(
        new TableFilter({ filters: [], logic: '' }, page, sizePerPage, {
            dir: 'asc',
            field: 'name',
        })
    );

    const handleTableChange = (_type: any, newState: any) => {
        const { page, sizePerPage, sortField, sortOrder } = newState;

        const updatedPostObj = new TableFilter(
            { filters: [], logic: '' },
            page,
            sizePerPage,
            { dir: sortOrder, field: sortField }
        );
        setCurrentSort(updatedPostObj);
    };

    const party = useCurrentMarketplaceParty(props.engagementId);

    const getEventsParty = (): TimelineParty => {
        if (party === TimelineParty.Buyer) {
            return TimelineParty.Buyer;
        }
        if (party === TimelineParty.VetValue) {
            return TimelineParty.VetValue;
        }
        return TimelineParty.Seller;
    };

    const fetchCurrentEvents = async () => {
        await dispatch(
            GetCurrentEvents({
                filterParams: currentSort,
                engagementId: props.engagementId,
                party: party,
            })
        );
    };

    useEffect(() => {
        setEventsParty(getEventsParty());
        fetchCurrentEvents()
            .then(() => {
                if (
                    location.state?.selectedEventId &&
                    props?.events.some(
                        (x) => x.id === location.state.selectedEventId
                    )
                ) {
                    window.scrollTo({
                        top: document.body.scrollHeight,
                        behavior: 'smooth',
                    });
                    window.history.replaceState({}, '');
                    const isClikable = isEventActionClickable(
                        location.state.selectedEventId,
                        false,
                        props.engagementData,
                        props.events,
                        party,
                        props.buyerActionState,
                        props.engagementProcess,
                        props.engagementBuyerId,
                        props.isFlowOfFundsAvailable
                    );
                    if (isClikable) {
                        props.onEventClick(location.state.selectedEventId);
                    }
                }
            })
            .finally(() => setIsLoading(false));
    }, [props.engagementId]);

    useEffect(() => {
        fetchCurrentEvents();
    }, [currentSort]);

    useEffect(() => {
        const filteredEvents = currentTimelineEvents.events.filter(
            (event) =>
                (event.party === eventsParty ||
                (event.party === TimelineParty.SellerAndBuyer &&
                    (party === TimelineParty.Seller ||
                        party === TimelineParty.Buyer)) ||
                (party === TimelineParty.Broker &&
                    event.linkedAction?.actionType ===
                        TimelineActionType.VetValueBuyerSelectionAndTeaserPosting)) &&
                !(party === TimelineParty.Buyer &&
                    event.linkedAction != null &&
                    props.buyerActionState?.some(
                        (as) =>
                            as.actionId === event.linkedAction.id &&
                            as.status === TimelineActionStatus.Completed
                    ))
        );
        if (
            props.opportunityStatus !==
                MarketplaceOpportunityStatus.LostOpportunity &&
            props.opportunityStatus !==
                MarketplaceOpportunityStatus.RejectedBySeller &&
            props.opportunityStatus !==
                MarketplaceOpportunityStatus.RejectedByBuyer
        ) {
            setEvents(filteredEvents);
        }
        setPage(currentTimelineEvents.pageNumber);
        setTotalSize(currentTimelineEvents.totalItems);
        setSizePerPage(currentTimelineEvents.pageSize);
    }, [currentTimelineEvents]);

    useEffect(() => {
        fetchCurrentEvents();
    }, [props.events]);

    const [expandedEventIds, setExpandedEventIds] = useState<string[]>([]);

    const getEventName = (cellContent: string, row: DocketRow) => {
        const project = props.dataRequestProjects
            ? props.dataRequestProjects?.find(
                  (p) => p.id == row.linkedAction?.referenceItemId
              )
            : undefined;

        const eventName = project?.actionLinkText ?? cellContent;
        return (
            <div className="docket-step-cell">
                <div
                    className={
                        'notification-flag-wrapper ' +
                        (row.isClickable ? 'linked-action' : '')
                    }
                    onClick={() => {
                        if (row.linkedAction && row.isClickable) {
                            props.onEventClick(row.id, row.timelineId);
                        }
                    }}>
                    {eventName}
                    <NotificationFlag
                        eventCode={`${props.notificationFlagPrefix}.${flagEventId(row)}`}/>
                </div>
                {row.buyersCount ? (
                    <span className="docket-buyers-count">
                        {row.buyersCount} {row.buyersCount === 1 ? 'buyer' : 'buyers'}
                    </span>
                ) : null}
            </div>
        );
    };

    const getStageName = (cellContent: string, row: DocketRow) => (
        <div>
            <div>{cellContent}</div>
            {buyerRowsToShow(row).map((sub) => (
                <div className="docket-buyer-line" key={`${sub.timelineId}-${sub.eventId}`}>
                    <span className="docket-buyer-name notification-flag-wrapper">
                        {formatBuyerDisplayName(sub.buyerCompanyName, sub.buyerAcronym)}
                        <NotificationFlag
                            eventCode={marketplaceFlagCodes.sellerDeadlineDocket(
                                { engagementId: props.engagementId, timelineId: sub.timelineId },
                                sub.eventId
                            )}
                        />
                    </span>
                </div>
            ))}
        </div>
    );

    const deadlineUtc = (event: TimelineEvent): Date =>
        getEventDeadlineDateUtc(props.engagementStartDate, event);

    // A buyer's sub-row is dated exactly like a row of the main: the same helper, fed that buyer's
    // own copy of the step.
    const asRowOfTheMain = (row: TimelineEvent, sub: DocketBuyerRow): TimelineEvent => ({
        ...row,
        dayStart: sub.dayStart,
        dayEnd: sub.dayEnd,
        startedAt: sub.startedAt,
        currentTempoDurationDays: sub.currentTempoDurationDays,
        closingLimitedDurationDays: sub.closingLimitedDurationDays,
        status: sub.status,
    });

    // A buyer step's row shows the deadline that comes first among the buyers walking it.
    const earliestDeadlineUtc = (row: TimelineEvent): Date => {
        const subRows = row.buyerRows ?? [];
        if (subRows.length === 0) {
            return deadlineUtc(row);
        }
        return subRows
            .map((sub) => deadlineUtc(asRowOfTheMain(row, sub)))
            .reduce((earliest, date) => (date < earliest ? date : earliest));
    };

    const toggleExpanded = (row: TimelineEvent) =>
        setExpandedEventIds((ids) =>
            ids.includes(row.id) ? ids.filter((id) => id !== row.id) : [...ids, row.id]
        );

    // [#1299 F1] Whether a step can be clicked depends on props.events, which can arrive after the rows (a waiting
    // Seller's docket switches to the catch-up's steps once they load). The remote table re-renders a cell only when
    // its row changes, so the answer travels in the row.
    // [#1314] A row of another timeline than the one in view opens that timeline, so it is clickable whatever its step.
    const isOfAnotherTimeline = (event: TimelineEvent) =>
        props.viewedTimelineId !== undefined &&
        event.timelineId !== undefined &&
        event.timelineId !== props.viewedTimelineId;

    const isRowClickable = (eventId: string) =>
        isEventActionClickable(
            eventId,
            !!props.isReadonly,
            props.engagementData,
            props.events,
            party,
            props.buyerActionState,
            props.engagementProcess,
            props.engagementBuyerId,
            props.isFlowOfFundsAvailable
        );

    const rows: DocketRow[] = events.map((event) => ({
        ...event,
        isExpanded: expandedEventIds.includes(event.id),
        isClickable: isOfAnotherTimeline(event) || isRowClickable(event.id),
    }));

    const buyerRowsToShow = (row: DocketRow): DocketBuyerRow[] =>
        row.isExpanded ? row.buyerRows ?? [] : [];

    const columns = [
        {
            dataField: 'expand',
            isDummyField: true,
            text: '',
            headerClasses: 'expand-column-header',
            classes: 'expand-column-cell',
            formatter: (_: unknown, row: DocketRow) =>
                row.buyerRows?.length ? (
                    <FontAwesomeIcon
                        className="docket-expand-chevron"
                        icon={row.isExpanded ? faChevronDown : faChevronRight}
                        onClick={() => toggleExpanded(row)}
                    />
                ) : null,
        },
        {
            dataField: 'dayEnd',
            text: 'Deadline',
            headerClasses: 'deadline-column-header',
            sort: true,
            formatter: (_: number, row: DocketRow) => {
                return (
                    <div>
                        <div>{formatBusinessDatePt(earliestDeadlineUtc(row))}</div>
                        {buyerRowsToShow(row).map((sub) => (
                            <div className="docket-buyer-line" key={`${sub.timelineId}-${sub.eventId}`}>
                                {formatBusinessDatePt(deadlineUtc(asRowOfTheMain(row, sub)))}
                            </div>
                        ))}
                    </div>
                );
            },
        },
        {
            dataField: 'stageName',
            text: 'Stage',
            headerClasses: 'stage-column-header',
            sort: true,
            formatter: (cellContent: string, row: DocketRow) =>
                getStageName(cellContent, row),
        },
        {
            dataField: 'name',
            text: 'Step',
            headerClasses: 'step-column-header',
            sort: true,
            formatter: (cellContent: string, row: DocketRow) =>
                getEventName(cellContent, row),
        },
    ];


    return (
        <div className="deadline-docket">
            <div className="body_inner_content">
                <div className="obpbq_row valuation_section">
                    <div className="sampledetail">
                        <div className="mb-3">
                            <strong>Deadline Docket</strong>
                        </div>
                        <div
                            role="tabpanel"
                            id="panel-0"
                            aria-labelledby="tab-0"
                            aria-hidden="false">
                            <div className="card-body">
                                {isLoading ? (
                                    Helpers.renderTableLoader()
                                ) : (
                                    <BootstrapTable
                                        striped
                                        bordered
                                        bootstrap4
                                        remote
                                        keyField="id"
                                        data={rows}
                                        columns={columns}
                                        defaultSorted={defaultSorted}
                                        filter={filterFactory()}
                                        pagination={paginationFactory({
                                            page,
                                            sizePerPage,
                                            totalSize,
                                            tooltipTargetId:
                                                'current-events-tooltip',
                                        })}
                                        onTableChange={handleTableChange}
                                        noDataIndication="No Events available"
                                        rowStyle={{ height: '44px' }}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DeadlineDocket;
